import axios from 'axios';
import bsCustomFileInput from 'bs-custom-file-input';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Button, Col, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { requestLogin } from '../../appStore/commonSlice';
import { AlertaDialog } from '../../components/CompUtils/Alerta';
import ErrorMsg from '../../components/CompUtils/ErrorMsg';
import FormSection from '../../components/CompUtils/FormSection';
import { finalize, init, setNotification } from '../../components/GlobalFeedback/feedbackSlice';
import { USERAPI } from '../../const';
import { desencriptar, encriptar } from '../../utils';
import es from '../../yup-es';
import { CampoFormik } from '../CompUtils';

//
// ─── SCHEMA ─────────────────────────────────────────────────────────────────────
//

yup.setLocale(es);

const schema = yup
  .object({
    nombre: yup.string().required(),
    apellido: yup.string().required(),
    //nombreUsuario: yup.string().required(),
    dni: yup.number().required().max(99999999, 'No debe superar los 8 dígitos'),
    cuil: yup.number().required().max(99999999999, 'No debe superar los 11 dígitos'),
    provincia: yup.number().required(),
    localidad: yup.string().required(),
    codPostal: yup
      .number()
      .required()
      .min(1000, 'Debe tener 4 dígitos')
      .max(9999, 'Debe tener 4 dígitos'),
    calle: yup.string().required(),
    numero: yup.number().required(),
    piso: yup.string(),
    departamento: yup.string(),
    email: yup.string().required().email(),
    emailAlternativo: yup
      .string()
      .oneOf([yup.ref('email'), null], 'Los emails no coinciden')
      .required('Debe completar la validacion de su email'),
    idNacionalidad: yup.string().required(),
    fechaNacimiento: yup
      .date()
      .required()
      .test('age', 'Debe ser mayor de 18 años', function (birthdate) {
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - 18);
        return birthdate <= cutoff;
      }),
    sexo: yup.string().required(),
    genero: yup.string().required(),
    telefono: yup.string().required(),
    telefonoAlternativo: yup.string(),
  })
  .shape(
    //Shape para evitar error de cyclic dependency al setear dependencias ciclicas con el cud
    {
      numCud: yup.string().when('cud', {
        is: (cud) => !!cud,
        then: yup.string().required(),
      }),
      cud: yup.mixed().when('numCud', {
        is: (numCud) => numCud && numCud.length > 0,
        then: yup.mixed().required(),
      }),
    },
    [['cud', 'numCud']],
  );

//
// ─── INITIAL VALUES ─────────────────────────────────────────────────────────────
//

//Por cada campo en el schema, defino su valor como string vacio o falso segun corresponda
const initialValues = Object.keys(schema.fields).reduce((acu, cur) => {
  return {
    [cur]: schema.fields[cur].describe().type === 'boolean' ? false : '',
    ...acu,
  };
}, {});
delete initialValues.cud;
initialValues.idNacionalidad = 13; //argentina default

//
// ─── COMPONENTE ─────────────────────────────────────────────────────────────────
//

const ProfileForm = () => {
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  const dispatch = useDispatch();
  let Hr61GyBgEdh3 = params.Hr61GyBgEdh3;
  const [alert, setAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const history = useHistory();
  const [nombreArchivo, setNombreArchivo] = useState('');

  const handlePaste = (e) => {
    e.preventDefault();
    window.alert(
      'Cuidado! No se puede pegar en el campo. Asegurate de que el mail ingresado sea correcto y tipea el mismo en ambos campos.',
    );
  };
  useEffect(() => {
    bsCustomFileInput.init();
    setNombreArchivo('Seleccione un archivo');
  }, []);

  const [, setShowModal] = useState(false);

  const toggleModal = (toggle) => {
    setErrorMsg('');
    setShowModal(toggle);
    if (!toggle) history.push('/');
  };

  const login = (cuil, password) => {
    dispatch(
      requestLogin({
        cuil,
        password,
      }),
    ).then((res) => {
      if (res.error) {
        setErrorMsg(res.error.message);
      } else {
        dispatch(
          setNotification({
            variant: 'success',
            msg: 'Login exitoso.',
          }),
        );
        toggleModal(false);

        const location = {
          pathname: '/',
        };
        history.push(location);
      }
    });
  };

  const handleRegistro = (values, actions) => {
    dispatch(init('Procesando el registro...'));
    //Declaro IIFE async
    (async () => {
      const formData = new FormData();
      const copy = Object.assign({}, values);

      if (values.cud) formData.append('cud', values.cud);

      delete copy.cud;
      formData.append('userData', JSON.stringify(copy));

      await axios.post(`${USERAPI}/auth/signup`, formData).catch((error) => {
        if (error.response) {
          setErrorMsg(error.response.data.message);
        } else if (error.request) {
          setErrorMsg(error.request.message);
        } else {
          setErrorMsg(error.message);
        }

        const location = {
          pathname: '/',
        };
        history.push(location);
        dispatch(finalize());

        throw error;
      });

      dispatch(finalize());
      setAlert(true);
    })();
  };

  useEffect(() => {
    if (Hr61GyBgEdh3) {
      let cuil = desencriptar(Hr61GyBgEdh3);
      cuil = cuil.split('&')[0].split('=')[1];
      initialValues.cuil = cuil;
    }
  }, [Hr61GyBgEdh3]);

  const redirectToHome = async () => {
    const parametros_url = await desencriptar(Hr61GyBgEdh3);

    const cuil = parametros_url.split('&')[0].split('=')[1];
    const password = await encriptar(cuil);

    await login(cuil, password);
  };
  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={handleRegistro}
        validateOnChange={false}
        initialValues={initialValues}>
        {(formik) => {
          return (
            <Form noValidate id="perfilForm" onSubmit={formik.handleSubmit}>
              <Form.Row>
                <CampoFormik
                  label="Nombres (obligatorio)"
                  name="nombre"
                  col={{ sm: 4 }}
                  formik={formik}
                />
                <CampoFormik
                  label="Apellidos (obligatorio)"
                  name="apellido"
                  col={{ sm: 4 }}
                  formik={formik}
                />
              </Form.Row>
              <Form.Row>
                <CampoFormik
                  label="D.N.I. (obligatorio)"
                  name="dni"
                  col={{ sm: 3 }}
                  formik={formik}
                />
                <CampoFormik
                  label="CUIT/CUIL (obligatorio)"
                  name="cuil"
                  col={{ sm: 3 }}
                  formik={formik}
                />
              </Form.Row>
              <FormSection>
                <Form.Row>
                  <CampoFormik
                    type="province"
                    label="Provincia (obligatorio)"
                    name="provincia"
                    col={{ sm: 4 }}
                    formik={formik}
                  />
                  <CampoFormik
                    type="localidad"
                    label="Localidad (obligatorio)"
                    name="localidad"
                    pcia={formik.values.provincia}
                    col={{ sm: 4 }}
                    formik={formik}
                  />
                  <CampoFormik
                    label="Código postal (obligatorio)"
                    name="codPostal"
                    col={{ sm: 2 }}
                    formik={formik}
                  />
                </Form.Row>
                <Form.Row>
                  <CampoFormik
                    label="Calle (obligatorio)"
                    name="calle"
                    col={{ sm: 4 }}
                    formik={formik}
                  />
                  <CampoFormik
                    label="Número (obligatorio)"
                    name="numero"
                    col={{ sm: 2 }}
                    formik={formik}
                  />
                  <CampoFormik label="Piso" name="piso" col={{ sm: 1 }} formik={formik} />
                  <CampoFormik
                    label="Departamento"
                    name="departamento"
                    col={{ sm: 1 }}
                    formik={formik}
                  />
                </Form.Row>
              </FormSection>
              <Form.Row>
                <CampoFormik
                  label="E-mail (obligatorio)"
                  name="email"
                  col={{ sm: 6 }}
                  formik={formik}
                />
                <CampoFormik
                  label="Ingresa tu E-mail nuevamente (obligatorio)"
                  name="emailAlternativo"
                  col={{ sm: 6 }}
                  formik={formik}
                  onPaste={(e) => handlePaste(e)}
                />
              </Form.Row>
              <Form.Row>
                <CampoFormik
                  type="pais"
                  label="Nacionalidad (obligatorio)"
                  col={{ sm: 5 }}
                  name="idNacionalidad"
                  formik={formik}
                />
                <CampoFormik
                  label="Fecha de nacimiento (obligatorio)"
                  name="fechaNacimiento"
                  col={{ sm: 3 }}
                  type="date2"
                  formik={formik}
                />
              </Form.Row>
              <Form.Row>
                <CampoFormik
                  type="sexo"
                  help="Como aparece en tu DNI"
                  label="Sexo (obligatorio)"
                  name="sexo"
                  col={{ sm: 4 }}
                  formik={formik}
                />
                <CampoFormik
                  type="genero"
                  label="Género"
                  name="genero"
                  col={{ sm: 8 }}
                  formik={formik}
                  value=""
                />
              </Form.Row>
              <Form.Row>
                <CampoFormik
                  col={{ sm: 4 }}
                  formik={formik}
                  label="Número de celular/Teléfono personal (obligatorio)"
                  name="telefono"
                  help="Incluya el código de área. Ejemplo: 011 1234 5678"
                />
                <CampoFormik
                  col={{ sm: 4 }}
                  formik={formik}
                  label="Número de celular/Teléfono alternativo"
                  name="telefonoAlternativo"
                  help="Incluya el código de área. Ejemplo: 011 1234 5678"
                />
              </Form.Row>
              <FormSection>
                <Form.Row className="align-items-center">
                  <Col sm={6}>
                    <CampoFormik
                      label="Número de Certificado Único de Discapacidad (CUD)."
                      name="numCud"
                      formik={formik}
                    />
                  </Col>
                  <Col sm={5}>
                    <Form.Group as={Col}>
                      <Form.Label>
                        Cargar CUD <i className="fa fa-wheelchair"></i>
                      </Form.Label>
                      <Form.File id="cud" custom>
                        <Form.File.Input
                          name="cud"
                          accept=".pdf,.doc,.odt,.jpg,.png"
                          type="file"
                          isInvalid={!!formik.errors.cud}
                          onChange={(ev) => {
                            formik.setFieldValue('cud', ev.currentTarget.files[0]);
                            setNombreArchivo(ev.currentTarget.files[0].name);
                          }}
                        />
                        <Form.File.Label data-browse="Cargar archivo">
                          {nombreArchivo}
                        </Form.File.Label>
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.cud}
                        </Form.Control.Feedback>
                      </Form.File>
                    </Form.Group>
                  </Col>
                  <Col sm={1}>
                    <Button
                      title="Eliminar datos CUD"
                      variant="danger"
                      className="text-center"
                      onClick={() => {
                        formik.setFieldValue('cud', undefined);
                        formik.setFieldValue('numCud', '');
                        setNombreArchivo('Seleccione un archivo');
                      }}>
                      X
                    </Button>
                  </Col>
                  <Form.Group as={Col} sm={12} style={{ marginTop: -10 }}>
                    <Form.Text className="help-block" style={{ fontSize: 12 }}>
                      Deberá especificar el número del Certificado Único de Discapacidad o demás
                      Certificados de Discapacidad vigentes, expedidos por autoridades competentes
                      según lo dispuesto en el Artículo 3º de la Ley Nº 22.431. - El formato de
                      archivo soportado es .PDF
                    </Form.Text>
                  </Form.Group>
                </Form.Row>
              </FormSection>
              <Form.Row>
                <Form.Group as={Col}>
                  <Button className="float-right" variant="success" type="submit">
                    Crear cuenta
                  </Button>
                  <ErrorMsg msg={errorMsg}></ErrorMsg>
                </Form.Group>
              </Form.Row>
            </Form>
          );
        }}
      </Formik>

      <AlertaDialog size="sm" show={alert} onClose={redirectToHome}>
        <div className="text-center">
          <i
            className="text-success fa fa-check-circle-o"
            style={{ fontSize: 64, WebkitTextStroke: '5px white' }}></i>
          <p>
            <span style={{ fontWeight: 'bold', fontSize: 24 }}>Registro Exitoso!</span>
            <br />
            Se ha registrado satisfactoriamente. En breve será redirigido a la página principal.
          </p>
        </div>
      </AlertaDialog>
    </>
  );
};

export default ProfileForm;
